import { useMemo } from 'react';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { Stack, Grid, Skeleton, Button, Typography, FormHelperText } from '@mui/material';
import { useSelector } from 'src/redux/hooks';
import { getDealSelector, getOfferPropertiesSelector } from 'src/redux/selectors/isoPortal';
import * as dealsApi from 'src/services/APIs/dealsApi';
import { useSignedLinkAuth } from 'src/hooks';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import { isAutomatedClosingEligible } from '../shared/automatedClosingUtils';

export const FundingControlsPanel = () => {
  const deal = useSelector(getDealSelector);
  const offerProperties = useSelector(getOfferPropertiesSelector);
  const { dealId, epk, et } = useSignedLinkAuth();
  const { enqueueSnackbar } = useSnackbar();

  const featureFlags = useFeatureFlags();
  const useAutomatedCheckout = useMemo(
    () => featureFlags.isFeatureEnabled(featureFlags.featureFlagKeys.useAutomatedCheckout),
    [featureFlags]
  );

  const canRequestFunding = useMemo(
    () =>
      offerProperties.hasLockedOffer &&
      ((deal?.state === 'closing' && deal?.subState === 'clear_to_close') ||
        (deal?.state === 'clear_to_close' && deal?.subState === 'ready_to_fund') ||
        deal?.state === 'Approved'),
    [deal?.state, deal?.subState, offerProperties.hasLockedOffer]
  );

  const canRequestAutomatedCheckout = useMemo(
    () =>
      offerProperties.hasLockedOffer &&
      ((deal?.state === 'closing' && deal?.subState === 'clear_to_close') ||
        (deal?.state === 'clear_to_close' && deal?.subState === 'ready_to_fund') ||
        deal?.state === 'Approved' ||
        deal?.state === 'contract_out'),
    [deal?.state, deal?.subState, offerProperties.hasLockedOffer]
  );

  const handleRequestFunding = async () => {
    try {
      if (!dealId) return false;

      const result = await dealsApi.postRunFunding({
        dealId,
        epk,
        et,
      });
      if (result?.result) {
        enqueueSnackbar('Funding request sent');
      } else {
        if (result?.messages?.length > 0) {
          enqueueSnackbar(result.messages[0], { variant: 'error' });
        } else {
          enqueueSnackbar('Error while requesting funding. Please try again!', {
            variant: 'error',
          });
        }
      }

      return true;
    } catch (error) {
      enqueueSnackbar('Error while trying to Request Funding', { variant: 'error' });
      return false;
    }
  };

  if (!deal) {
    return <Skeleton width={'100%'} height={'44px'} />;
  }

  const handleSendToAutomatedClosing = async () => {
    try {
      if (!dealId) return false;

      const result = await dealsApi.postSendToAutomatedClosing({
        dealId,
        epk,
        et,
      });
      if (result?.result) {
        enqueueSnackbar('Customer Sent to Automated Closing');
      } else {
        if (result?.messages?.length > 0) {
          enqueueSnackbar(result.messages[0], { variant: 'error' });
        } else {
          enqueueSnackbar('Error while requesting automated closing. Please try again!', {
            variant: 'error',
          });
        }
      }

      return true;
    } catch (error) {
      enqueueSnackbar('Error while trying to send to automated closing', { variant: 'error' });
      return false;
    }
  };

  return (
    <Stack direction="column" alignItems="center" justifyContent="space-between">
      <Grid container p={1} pb={0}>
        <Grid item xs={12}>
          {useAutomatedCheckout && isAutomatedClosingEligible(offerProperties) ? (
            <>
              <FormHelperText>
                This offer is eligible for online checkout by the merchant. You can direct the
                merchant to online checkout or use the regular closing process that is assisted by
                Libertas.
              </FormHelperText>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 2 }}>
                Online Closing & Funding*
              </Typography>
              <Button
                variant="contained"
                onClick={handleSendToAutomatedClosing}
                fullWidth={true}
                disabled={!canRequestAutomatedCheckout}
              >
                Send Customer to Online Checkout
              </Button>
              <FormHelperText>
                *Clicking will direct the <b>merchant</b> to the Libertas Customer Portal.
                <u>
                  <b>The merchant must be the one to complete the process.</b>
                </u>
              </FormHelperText>

              <Typography variant="body1" sx={{ fontWeight: 'bold' }} mt={3} mb={1}>
                Assisted Closing & Funding
              </Typography>

              <Button
                variant="contained"
                onClick={handleRequestFunding}
                fullWidth={true}
                disabled={!canRequestFunding}
                sx={{ mb: 1 }}
              >
                Request Funding
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleRequestFunding}
              fullWidth={true}
              disabled={!canRequestFunding}
              sx={{ mb: 1 }}
            >
              Request Funding
            </Button>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
